import { LocationQueryValue } from 'vue-router'

/**
 * Longueur maximale acceptée pour un chemin de redirection interne.
 * Au-delà, la valeur ne vient pas d'une navigation légitime.
 */
const MAXIMUM_REDIRECT_PATH_LENGTH = 2048

/**
 * Longueur maximale acceptée pour une adresse e-mail transmise par une application
 * appelante : la limite d'une adresse selon la RFC 5321.
 */
const MAXIMUM_EMAIL_HINT_LENGTH = 254

/**
 * Bornes des caractères de contrôle, interdits dans un chemin de redirection :
 * plage C0 (`\u0000`-`\u001F`) et DEL (`\u007F`).
 */
const FIRST_PRINTABLE_CHARACTER_CODE = 0x20
const DELETE_CHARACTER_CODE = 0x7F

/**
 * Vrai dès qu'un caractère de contrôle apparaît dans la valeur.
 *
 * Testé caractère par caractère plutôt que par une expression régulière : une classe
 * de caractères de contrôle littérale déclenche `no-control-regex`, et la désactiver
 * masquerait aussi les vrais oublis.
 */
function containsControlCharacter (candidateValue: string): boolean {
  for (const character of candidateValue) {
    const characterCode = character.codePointAt(0) as number

    if (characterCode < FIRST_PRINTABLE_CHARACTER_CODE || characterCode === DELETE_CHARACTER_CODE) {
      return true
    }
  }

  return false
}

/**
 * Seule forme de test de présence autorisée dans le chantier de redirection :
 * une chaîne vide ou faite d'espaces n'est pas une valeur.
 */
export function isNonEmptyString (candidateValue: unknown): boolean {
  return typeof candidateValue === 'string' && candidateValue.trim().length > 0
}

/**
 * Lit un paramètre de query string en une valeur unique.
 *
 * Un paramètre peut légitimement arriver en tableau (`?state=a&state=b`) : on retient
 * la première occurrence exploitable, et rien d'autre. Seul point d'entrée autorisé
 * pour lire `route.query`.
 */
export function readSingleQueryParameterValue (
  queryValue: LocationQueryValue | LocationQueryValue[] | undefined
): string | null {
  if (Array.isArray(queryValue)) {
    const firstUsableValue = queryValue.find((currentValue) => isNonEmptyString(currentValue))
    return isNonEmptyString(firstUsableValue) ? (firstUsableValue as string) : null
  }

  return isNonEmptyString(queryValue) ? (queryValue as string) : null
}

/**
 * Vrai si le candidat est un chemin interne à cette application, et rien d'autre.
 *
 * Rejette les URL absolues (`https://evil.tld`), les URL protocol-relative
 * (`//evil.tld`), les variantes à antislash (`/\evil.tld`, traitées comme `//` par
 * certains navigateurs) et les échappements qui masquent l'une de ces formes
 * (`%2f%2fevil.tld`). Sans ce contrôle, la valeur part telle quelle dans
 * `router.push({ path })` et l'utilisateur quitte le domaine.
 */
export function isSafeInternalRedirectPath (candidatePath: string): boolean {
  if (!isNonEmptyString(candidatePath)) {
    return false
  }

  if (candidatePath.length > MAXIMUM_REDIRECT_PATH_LENGTH) {
    return false
  }

  if (!hasSafeInternalPathShape(candidatePath)) {
    return false
  }

  // Un chemin encodé doit rester sûr une fois décodé : `%2f%2fevil.tld` devient `//evil.tld`.
  let decodedPath: string
  try {
    decodedPath = decodeURIComponent(candidatePath)
  } catch (error) {
    return false
  }

  return hasSafeInternalPathShape(decodedPath)
}

/**
 * Contrôle de forme appliqué au chemin brut puis à sa version décodée.
 */
function hasSafeInternalPathShape (candidatePath: string): boolean {
  if (!candidatePath.startsWith('/')) {
    return false
  }

  if (candidatePath.startsWith('//')) {
    return false
  }

  if (candidatePath.includes('\\')) {
    return false
  }

  return !containsControlCharacter(candidatePath)
}

/**
 * Vrai si le candidat est exploitable comme pré-remplissage du champ e-mail.
 *
 * Contrôle de forme minimal, et volontairement pas une expression régulière d'adresse
 * exhaustive : la valeur vient d'une application appelante, ne sert qu'à garnir un
 * champ que `checkEmail` interrogera de toute façon côté API, et c'est cette API qui
 * décide si l'adresse existe. Ce qu'on refuse ici, c'est ce qui n'a rien à faire dans
 * un champ de saisie — caractère de contrôle, longueur d'attaque, valeur qui n'est
 * visiblement pas une adresse.
 */
export function isSafeEmailHint (candidateValue: string): boolean {
  if (!isNonEmptyString(candidateValue)) {
    return false
  }

  const trimmedValue = candidateValue.trim()

  if (trimmedValue.length > MAXIMUM_EMAIL_HINT_LENGTH) {
    return false
  }

  if (containsControlCharacter(trimmedValue)) {
    return false
  }

  const atSignParts = trimmedValue.split('@')

  return atSignParts.length === 2 &&
    atSignParts[0].length > 0 &&
    atSignParts[1].length > 0 &&
    !trimmedValue.includes(' ')
}
